import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiPlus, FiEdit2, FiSearch, FiX, FiCalendar, FiClock, FiList, FiAlertCircle, FiFilter, FiUpload, FiCheckCircle, FiChevronDown } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth } from '../../finance/auth/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

const LEAD_STAGES = ['LEAD', 'TALKS', 'MEETING', 'NEGOTIATIONS', 'MQL', 'SQL', 'CONVERSION_TO_CLIENT'];

export default function SalesLeads() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all'); // all, today, overdue, search
  const [searchQuery, setSearchQuery] = useState('');
  const [leadStatusFilter, setLeadStatusFilter] = useState('');
  const [verticalFilter, setVerticalFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [products, setProducts] = useState([]);
  const [editingLead, setEditingLead] = useState(null);

  // Form State
  const initialFormState = {
    firstName: '', lastName: '', jobTitle: '', companyName: '', email: '', 
    numberOfEmployees: '', industrySegment: '', linkedinUrl: '', websiteUrl: '', 
    countryRegion: '', annualRevenue: '', followUpDate: '', ownerType: '', 
    contactOwner: '', companyOwner: '',
    verticalInterest: '', linkedinConnectionStatus: '', 
    linkedinDmStatus: '', lifeCycleStage: '', leadStatus: 'LEAD',
    assignedTeamMemberId: user?.id || 1
  };
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);

  // ── Excel Upload state ───────────────────────────────────────────────
  const [uploadResult, setUploadResult]     = useState(null); // { totalRows, imported, skipped, errors }
  const [isDragging, setIsDragging]         = useState(false);
  const fileInputRef = useRef(null);

  const EXPECTED_HEADERS = [
    'First Name','Last Name','Job title','Company Name','Email',
    'Number of employees','Industry Segment','Linkedin URL','website URL',
    'Country/Region','Annual revenue','LinkedIn connection Request Date',
    'Owner Type','Contact Owner','Company Owner','Life cycle stage',
    'Lead Status','Vertical Interest','LinkedIn Connection Status','LinkedIn DM status'
  ];

  const openUploadModal = () => {
    setIsUploadOpen(true);
    setUploadFile(null);
    setUploadProgress(0);
    setUploadResult(null);
    setIsDragging(false);
  };

  const closeUploadModal = () => {
    if (uploading) return;
    setIsUploadOpen(false);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) validateAndSetFile(f);
  };

  const handleFileInput = (e) => {
    const f = e.target.files[0];
    if (f) validateAndSetFile(f);
  };

  const validateAndSetFile = (f) => {
    if (!f.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Only .xlsx / .xls files are supported');
      return;
    }
    setUploadFile(f);
    setUploadResult(null);
    setUploadProgress(0);
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile || !user?.token) return;
    try {
      setUploading(true);
      setUploadProgress(10);
      const formData = new FormData();
      formData.append('file', uploadFile);
      setUploadProgress(30);
      const res = await axios.post(
        `${API_BASE_URL}/sales/leads/upload`,
        formData,
        {
          headers: {
            Authorization: `${user?.tokenType || 'Bearer'} ${user.token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (p) => {
            setUploadProgress(Math.round(30 + (p.loaded / p.total) * 50));
          }
        }
      );
      setUploadProgress(100);
      const result = res.data?.data;
      setUploadResult(result);
      toast.success(`${result?.imported ?? 0} leads imported successfully!`);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const headers = { Authorization: `${user?.tokenType || 'Bearer'} ${user.token}` };
        const res = await axios.get(`${API_BASE_URL}/product/getAllProducts`, { headers });
        setProducts(Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.content || []));
      } catch (err) {
        console.error("Failed to load products", err);
      }
    };
    if (user?.token) {
        fetchLeads();
        fetchProducts();
    }
  }, [user, activeFilter, searchQuery, leadStatusFilter]);

  const fetchLeads = async (searchOverride = '') => {
    if (!user?.token) return;
    
    try {
      setLoading(true);
      const headers = { Authorization: `${user?.tokenType || 'Bearer'} ${user.token}` };
      
      let url = `${API_BASE_URL}/sales/leads`;
      
      if (searchOverride || (activeFilter === 'search' && searchQuery)) {
        url = `${API_BASE_URL}/sales/leads/search?q=${searchOverride || searchQuery}`;
      } else if (activeFilter === 'today') {
        url = `${API_BASE_URL}/sales/leads/followup/today`;
      } else if (activeFilter === 'overdue') {
        url = `${API_BASE_URL}/sales/leads/followup/overdue`;
      } else if (leadStatusFilter) {
        url = `${API_BASE_URL}/sales/leads/stage/${leadStatusFilter}`;
      }
      
      const res = await axios.get(url, { headers });
      setLeads(res?.data?.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setActiveFilter('search');
      setLeadStatusFilter('');
      fetchLeads(searchQuery);
    } else {
      setActiveFilter('all');
    }
  };

  const handleQuickLeadStatusChange = async (leadId, newLeadStatus, currentLead) => {
    try {
      const headers = { Authorization: `${user?.tokenType || 'Bearer'} ${user.token}` };
      const payload = {
        firstName: currentLead.firstName, lastName: currentLead.lastName, jobTitle: currentLead.jobTitle,
        companyName: currentLead.companyName, email: currentLead.email, numberOfEmployees: currentLead.numberOfEmployees ? parseInt(currentLead.numberOfEmployees) : null,
        industrySegment: currentLead.industrySegment, linkedinUrl: currentLead.linkedinUrl, websiteUrl: currentLead.websiteUrl,
        countryRegion: currentLead.countryRegion, annualRevenue: currentLead.annualRevenue, 
        linkedinConnectionRequestDate: currentLead.linkedinConnectionRequestDate || null, ownerType: currentLead.ownerType,
        contactOwner: currentLead.contactOwner, companyOwner: currentLead.companyOwner,
        verticalInterest: currentLead.verticalInterest, 
        linkedinConnectionStatus: currentLead.linkedinConnectionStatus, linkedinDmStatus: currentLead.linkedinDmStatus,
        followUpDate: currentLead.followUpDate || null, assignedTeamMemberId: currentLead.assignedTeamMemberId ? parseInt(currentLead.assignedTeamMemberId) : user?.id || 1,
        leadStatus: newLeadStatus
      };
      
      await axios.put(`${API_BASE_URL}/sales/leads/${leadId}`, payload, { headers });
      toast.success('Stage updated');
      setLeads(leads.map(l => l.id === leadId ? { ...l, leadStatus: newLeadStatus } : l));
    } catch (err) {
      console.error(err);
      toast.error('Failed to update stage');
    }
  };


  const handleQuickFollowUpChange = async (leadId, newDate, currentLead) => {
    try {
      const headers = { Authorization: `${user?.tokenType || 'Bearer'} ${user.token}` };
      const payload = {
        firstName: currentLead.firstName, lastName: currentLead.lastName, jobTitle: currentLead.jobTitle,
        companyName: currentLead.companyName, email: currentLead.email, numberOfEmployees: currentLead.numberOfEmployees ? parseInt(currentLead.numberOfEmployees) : null,
        industrySegment: currentLead.industrySegment, linkedinUrl: currentLead.linkedinUrl, websiteUrl: currentLead.websiteUrl,
        countryRegion: currentLead.countryRegion, annualRevenue: currentLead.annualRevenue, 
        linkedinConnectionRequestDate: currentLead.linkedinConnectionRequestDate || null, ownerType: currentLead.ownerType,
        contactOwner: currentLead.contactOwner, companyOwner: currentLead.companyOwner,
        verticalInterest: currentLead.verticalInterest, 
        linkedinConnectionStatus: currentLead.linkedinConnectionStatus, linkedinDmStatus: currentLead.linkedinDmStatus,
        followUpDate: newDate || null, assignedTeamMemberId: currentLead.assignedTeamMemberId ? parseInt(currentLead.assignedTeamMemberId) : user?.id || 1,
        leadStatus: currentLead.leadStatus
      };
      
      await axios.put(`${API_BASE_URL}/sales/leads/${leadId}`, payload, { headers });
      toast.success('Follow-up date updated');
      setLeads(leads.map(l => l.id === leadId ? { ...l, followUpDate: newDate } : l));
    } catch (err) {
      console.error(err);
      toast.error('Failed to update follow-up date');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getAssignedEmp = (lead) => {
    if (!lead) return '—';
    if (lead.contactOwner && lead.contactOwner.trim()) {
      return lead.contactOwner.trim();
    }
    if (lead.assignedTeamMemberName && lead.assignedTeamMemberName.trim()) {
      return lead.assignedTeamMemberName.trim();
    }
    if (lead.companyOwner && lead.companyOwner.trim()) {
      return lead.companyOwner.trim();
    }
    if (lead.assignedTeamMemberId) {
      if (user?.id && lead.assignedTeamMemberId === user.id) {
        return user.username || 'You';
      }
      return `Emp #${lead.assignedTeamMemberId}`;
    }
    return '—';
  };

  const openCreateModal = () => {
    setEditingLead(null);
    setFormData({
      ...initialFormState,
      contactOwner: user?.username || '',
      assignedTeamMemberId: user?.id || 1
    });
    setIsModalOpen(true);
  };

  const openEditModal = (lead) => {
    setEditingLead(lead);
    setFormData({
      firstName: lead.firstName || '', lastName: lead.lastName || '', jobTitle: lead.jobTitle || '',
      companyName: lead.companyName || '', email: lead.email || '', numberOfEmployees: lead.numberOfEmployees || '',
      industrySegment: lead.industrySegment || '', linkedinUrl: lead.linkedinUrl || '', websiteUrl: lead.websiteUrl || '',
      countryRegion: lead.countryRegion || '', annualRevenue: lead.annualRevenue || '', 
      linkedinConnectionRequestDate: lead.linkedinConnectionRequestDate || '', ownerType: lead.ownerType || '',
      contactOwner: lead.contactOwner || '', companyOwner: lead.companyOwner || '',
      verticalInterest: lead.verticalInterest || '', 
      linkedinConnectionStatus: lead.linkedinConnectionStatus || '', linkedinDmStatus: lead.linkedinDmStatus || '',
      leadStatus: lead.leadStatus || 'LEAD', assignedTeamMemberId: lead.assignedTeamMemberId || user?.id || 1
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.token) return;

    try {
      setSubmitting(true);
      const headers = { Authorization: `${user?.tokenType || 'Bearer'} ${user.token}` };
      
      const payload = {
        ...formData,
        numberOfEmployees: formData.numberOfEmployees ? parseInt(formData.numberOfEmployees) : null,
        assignedTeamMemberId: formData.assignedTeamMemberId ? parseInt(formData.assignedTeamMemberId) : 1
      };

      if (editingLead) {
        await axios.put(`${API_BASE_URL}/sales/leads/${editingLead.id}`, payload, { headers });
        toast.success('Lead updated successfully');
      } else {
        await axios.post(`${API_BASE_URL}/sales/leads`, payload, { headers });
        toast.success('Lead created successfully');
      }
      
      setIsModalOpen(false);
      fetchLeads();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to save lead');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full font-sans text-slate-800 p-6 md:p-8 pb-12">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lead Pipeline</h1>
          <p className="text-sm text-slate-500 mt-1">Manage and track your ongoing deals.</p>
        </div>
        
        <div className="grid grid-cols-2 sm:flex items-center gap-2.5 sm:gap-3 w-full sm:w-auto">
          {/* Upload Excel Button */}
          <button
            onClick={openUploadModal}
            className="flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 hover:border-slate-400 px-4 sm:px-5 py-2.5 rounded-lg text-xs sm:text-sm font-medium shadow-sm transition-all active:scale-95 cursor-pointer"
          >
            <FiUpload size={16} /> <span className="truncate">Upload Excel</span>
          </button>
          <button
            onClick={openCreateModal}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-5 py-2.5 rounded-lg text-xs sm:text-sm font-medium shadow-sm transition-all active:scale-95 cursor-pointer"
          >
            <FiPlus size={18} /> <span>New Lead</span>
          </button>
        </div>
      </div>

      {/* Filters & Search Bar */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        
        {/* Horizontal Quick Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 lg:pb-0 hide-scrollbar">
          <button 
            onClick={() => { setActiveFilter('all'); setVerticalFilter(''); setLeadStatusFilter(''); setSearchQuery(''); }} 
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeFilter === 'all' && !verticalFilter && !leadStatusFilter ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            <FiList size={16} /> All Leads
          </button>
          <button 
            onClick={() => { setActiveFilter('today'); setVerticalFilter(''); setLeadStatusFilter(''); setSearchQuery(''); }} 
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeFilter === 'today' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            <FiCalendar size={16} /> Follow-ups Today
          </button>
          <button 
            onClick={() => { setActiveFilter('overdue'); setVerticalFilter(''); setLeadStatusFilter(''); setSearchQuery(''); }} 
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeFilter === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            <FiAlertCircle size={16} /> Overdue
          </button>
        </div>

        {/* Search & Vertical Dropdown */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 w-full lg:w-auto">
          <div className="relative w-full sm:w-auto">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
              <FiFilter size={16} />
            </div>
            <select
              value={verticalFilter}
              onChange={(e) => { setVerticalFilter(e.target.value); setActiveFilter('all'); setSearchQuery(''); }}
              className="w-full sm:w-auto pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none text-slate-700 transition-colors hover:bg-slate-100 cursor-pointer"
            >
              <option value="">All Verticals</option>
              {products.map(p => (
                <option key={p.id} value={p.productname}>{p.productname}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
              <FiChevronDown size={14} />
            </div>
          </div>

          <form onSubmit={handleSearchSubmit} className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
              <FiSearch size={16} />
            </div>
            <input
              type="text"
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all placeholder-slate-400"
            />
          </form>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-16 text-center text-slate-500 font-medium animate-pulse">Fetching latest pipeline data...</div>
        ) : (verticalFilter ? leads.filter(l => (l.verticalInterest || '') === verticalFilter) : leads).length === 0 ? (
          <div className="p-16 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-50 text-slate-300 mb-4 border border-slate-100">
              <FiList size={28} />
            </div>
            <h3 className="text-lg font-bold text-slate-900">No leads found</h3>
            <p className="text-slate-500 mt-1 text-sm">Try adjusting your filters or search query.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Desktop Table (Hidden on Mobile) */}
            <table className="hidden md:table w-full min-w-[700px] text-left text-sm text-slate-600">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="px-6 py-4">Lead Detail</th>
                  <th className="px-6 py-4">Company</th>
                  <th className="px-6 py-4">Assigned Emp</th>
                  <th className="px-6 py-4">Follow-up</th>
                  <th className="px-6 py-4">Vertical</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(verticalFilter ? leads.filter(l => (l.verticalInterest || '') === verticalFilter) : leads).map(lead => {
                  const assignedEmp = getAssignedEmp(lead);
                  return (
                    <tr 
                      key={lead.id} 
                      onClick={() => navigate(`/sales/leads/${lead.id}`)}
                      className="hover:bg-slate-50 transition-colors group cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                          {lead.firstName} {lead.lastName}
                        </div>
                        <p className="text-xs text-blue-600 mt-0.5">{lead.email || 'No email'}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-slate-700 font-medium">
                        {lead.companyName || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {assignedEmp && assignedEmp !== '—' ? (
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-slate-100 border border-slate-200 text-slate-700 flex items-center justify-center text-xs font-bold uppercase shadow-2xs">
                              {assignedEmp.charAt(0)}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-xs font-semibold text-slate-800" title={assignedEmp}>{assignedEmp}</span>
                              {lead.ownerType && (
                                <span className="text-[10px] text-slate-400 capitalize">{lead.ownerType}</span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 font-normal">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="relative group/date">
                          <input 
                            type="date"
                            value={lead.followUpDate || ''}
                            onChange={(e) => handleQuickFollowUpChange(lead.id, e.target.value, lead)}
                            className={`text-xs font-medium bg-transparent outline-none cursor-pointer border-b border-transparent hover:border-slate-300 transition-colors ${!lead.followUpDate ? 'text-slate-400' : new Date(lead.followUpDate) < new Date(new Date().setHours(0,0,0,0)) ? 'text-red-600 font-bold' : 'text-slate-700'}`}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {lead.verticalInterest ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                            {lead.verticalInterest}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400 font-normal">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        <button 
                          onClick={() => openEditModal(lead)}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-100"
                          title="Full Edit"
                        >
                          <FiEdit2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Cards View (Visible on Mobile Only) */}
            <div className="block md:hidden divide-y divide-slate-100">
              {(verticalFilter ? leads.filter(l => (l.verticalInterest || '') === verticalFilter) : leads).map(lead => {
                const assignedEmp = getAssignedEmp(lead);
                const isOverdue = lead.followUpDate && new Date(lead.followUpDate) < new Date(new Date().setHours(0,0,0,0));
                return (
                  <div 
                    key={lead.id}
                    onClick={() => navigate(`/sales/leads/${lead.id}`)}
                    className="p-4 space-y-2.5 hover:bg-slate-50/70 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 text-sm hover:text-blue-600 transition-colors truncate">
                          {lead.firstName} {lead.lastName}
                        </div>
                        <p className="text-xs text-blue-600 truncate mt-0.5">{lead.email || 'No email'}</p>
                      </div>

                      <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border shrink-0 ${
                        lead.leadStatus === 'CONVERSION_TO_CLIENT' ? 'bg-emerald-100 text-emerald-700 border-emerald-200/60' : 
                        lead.leadStatus === 'NEGOTIATIONS' ? 'bg-purple-100 text-purple-700 border-purple-200/60' : 
                        lead.leadStatus === 'MEETING' ? 'bg-amber-100 text-amber-700 border-amber-200/60' : 
                        lead.leadStatus === 'TALKS' ? 'bg-indigo-100 text-indigo-700 border-indigo-200/60' : 
                        lead.leadStatus === 'SQL' ? 'bg-blue-100 text-blue-700 border-blue-200/60' :
                        lead.leadStatus === 'MQL' ? 'bg-sky-100 text-sky-700 border-sky-200/60' :
                        'bg-slate-100 text-slate-700 border-slate-200/60'
                      }`}>
                        {lead.leadStatus === 'CONVERSION_TO_CLIENT' ? 'CLIENT' : lead.leadStatus}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-600 gap-2">
                      <span className="font-medium text-slate-700 truncate">{lead.companyName || '—'}</span>
                      {lead.verticalInterest && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 shrink-0">
                          {lead.verticalInterest}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-1 border-t border-slate-100/80 text-xs">
                      <div className="flex items-center gap-1.5 text-slate-500">
                        <span className="text-[11px] text-slate-400">Owner:</span>
                        <span className="font-semibold text-slate-700 text-[11px] truncate max-w-[100px]">{assignedEmp}</span>
                      </div>

                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <input 
                          type="date"
                          value={lead.followUpDate || ''}
                          onChange={(e) => handleQuickFollowUpChange(lead.id, e.target.value, lead)}
                          className={`text-xs font-semibold bg-transparent outline-none cursor-pointer border-b border-transparent hover:border-slate-300 transition-colors ${
                            !lead.followUpDate ? 'text-slate-400' : isOverdue ? 'text-rose-600 font-bold' : 'text-slate-700'
                          }`}
                        />
                        <button 
                          onClick={() => openEditModal(lead)}
                          className="p-1 text-slate-400 hover:text-blue-600 rounded transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col relative overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-white z-10">
              <h2 className="text-xl font-semibold text-slate-800">
                {editingLead ? 'Edit Lead' : 'Create New Lead'}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <FiX size={24} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
              <form id="lead-form" onSubmit={handleSubmit} className="space-y-8">
                
                {/* Primary Information */}
                <div>
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4">Primary Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">First Name *</label>
                      <input required type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Last Name *</label>
                      <input required type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Stage</label>
                      <select name="leadStatus" value={formData.leadStatus} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer">
                        {LEAD_STAGES.map(s => <option key={s} value={s}>{s === 'CONVERSION_TO_CLIENT' ? 'CONVERSION TO CLIENT' : s}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Company details */}
                <div>
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 border-t border-slate-100 pt-6">Company & Firmographics</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Name</label>
                      <input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Job Title</label>
                      <input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Industry Segment</label>
                      <input type="text" name="industrySegment" value={formData.industrySegment} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Number of Employees</label>
                      <input type="number" name="numberOfEmployees" value={formData.numberOfEmployees} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Annual Revenue</label>
                      <input type="text" name="annualRevenue" value={formData.annualRevenue} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Region / Country</label>
                      <input type="text" name="countryRegion" value={formData.countryRegion} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Website URL</label>
                      <input type="url" name="websiteUrl" value={formData.websiteUrl} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">LinkedIn Profile URL</label>
                      <input type="url" name="linkedinUrl" value={formData.linkedinUrl} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                  </div>
                </div>

                {/* Tracking info */}
                <div>
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 border-t border-slate-100 pt-6">Sales Tracking & Ownership</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Follow-up Date</label>
                      <input type="date" name="followUpDate" value={formData.followUpDate} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Owner Type</label>
                      <input type="text" name="ownerType" value={formData.ownerType} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Contact Owner</label>
                      <input type="text" name="contactOwner" value={formData.contactOwner} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Owner</label>
                      <input type="text" name="companyOwner" value={formData.companyOwner} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Lifecycle Stage</label>
                      <input type="text" name="lifeCycleStage" value={formData.lifeCycleStage} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Vertical Interest</label>
                      <select name="verticalInterest" value={formData.verticalInterest} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer">
                        <option value="">Select Product...</option>
                        {products.map(p => (
                            <option key={p.id} value={p.productname}>{p.productname}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 border-t border-slate-100 pt-6">LinkedIn Outreach</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Connection Req Date</label>
                      <input type="date" name="linkedinConnectionRequestDate" value={formData.linkedinConnectionRequestDate} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Connection Status</label>
                      <select name="linkedinConnectionStatus" value={formData.linkedinConnectionStatus} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer">
                        <option value="">Select Status...</option>
                        <option value="pending">Pending</option>
                        <option value="accepted">Accepted</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">DM Status</label>
                      <select name="linkedinDmStatus" value={formData.linkedinDmStatus} onChange={handleInputChange} className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all cursor-pointer">
                        <option value="">Select Status...</option>
                        <option value="not sent">Not sent</option>
                        <option value="sent">Sent</option>
                        <option value="replied">Replied</option>
                      </select>
                    </div>
                  </div>
                </div>

              </form>
            </div>
            
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 z-10">
              <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 text-sm font-medium text-slate-700 hover:text-slate-900 bg-white border border-slate-300 shadow-sm hover:bg-slate-50 rounded-lg transition-colors">Cancel</button>
              <button type="submit" form="lead-form" disabled={submitting} className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-sm rounded-lg disabled:opacity-50 transition-colors">
                {submitting ? 'Saving...' : (editingLead ? 'Save Changes' : 'Create Lead')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Excel Upload Modal ──────────────────────────────────────────── */}
      {isUploadOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) closeUploadModal(); }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-blue-600">
              <div className="flex items-center gap-2 text-white">
                <FiUpload size={18} />
                <h2 className="text-base font-semibold">Upload Leads Excel</h2>
              </div>
              <button onClick={closeUploadModal} className="text-white/80 hover:text-white transition-colors" disabled={uploading}>
                <FiX size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto max-h-[75vh]">

              {/* Drop Zone */}
              {!uploadResult && (
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleFileDrop}
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                    isDragging ? 'border-blue-500 bg-blue-50' :
                    uploadFile ? 'border-emerald-400 bg-emerald-50' :
                    'border-slate-300 hover:border-blue-400 hover:bg-blue-50/40'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={handleFileInput}
                  />
                  {uploadFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <FiCheckCircle size={32} className="text-emerald-500" />
                      <p className="font-semibold text-slate-800 text-sm">{uploadFile.name}</p>
                      <p className="text-xs text-slate-500">{(uploadFile.size / 1024).toFixed(1)} KB — ready to upload</p>
                      <button
                        onClick={(e) => { e.stopPropagation(); setUploadFile(null); }}
                        className="text-xs text-slate-400 hover:text-red-500 underline mt-1"
                      >Remove</button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-slate-500">
                      <FiUpload size={32} className="text-slate-300" />
                      <p className="font-medium text-slate-700 text-sm">Drag & drop your Excel file here</p>
                      <p className="text-xs">or click to browse &nbsp;·&nbsp; .xlsx / .xls only</p>
                    </div>
                  )}
                </div>
              )}

              {/* Progress Bar */}
              {uploading && (
                <div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Uploading & processing…</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 bg-blue-500 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Upload Result */}
              {uploadResult && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-slate-800">{uploadResult.totalRows}</p>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mt-1">Total Rows</p>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-600">{uploadResult.imported}</p>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-400 mt-1">Imported</p>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-orange-500">{uploadResult.skipped}</p>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-orange-300 mt-1">Skipped</p>
                    </div>
                  </div>

                  {uploadResult.errors?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <p className="text-xs font-bold text-red-600 uppercase tracking-wider mb-2">Row Errors</p>
                      <ul className="list-disc list-inside space-y-1">
                        {uploadResult.errors.map((err, i) => (
                          <li key={i} className="text-xs text-red-700">{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={() => { setUploadFile(null); setUploadResult(null); setUploadProgress(0); }}
                    className="w-full text-sm text-slate-500 hover:text-blue-600 underline text-center"
                  >Upload another file</button>
                </div>
              )}

              {/* Expected Headers Reference */}
              {!uploadResult && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Expected column headers</p>
                  <div className="flex flex-wrap gap-1.5">
                    {EXPECTED_HEADERS.map(h => (
                      <span key={h} className="px-2 py-0.5 bg-blue-50 border border-blue-100 text-blue-700 rounded text-[11px] font-medium">{h}</span>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">Headers matched by name. <strong>Stage</strong> column optional — defaults to LEAD.</p>
                </div>
              )}
            </div>

            {/* Footer */}
            {!uploadResult && (
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
                <button
                  onClick={closeUploadModal}
                  disabled={uploading}
                  className="px-5 py-2.5 text-sm font-medium text-slate-700 hover:text-slate-900 bg-white border border-slate-300 shadow-sm hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-50"
                >Cancel</button>
                <button
                  onClick={handleUploadSubmit}
                  disabled={!uploadFile || uploading}
                  className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-sm rounded-lg disabled:opacity-40 transition-colors"
                >
                  <FiUpload size={15} />
                  {uploading ? 'Importing…' : 'Upload & Import'}
                </button>
              </div>
            )}
            {uploadResult && (
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                <button
                  onClick={closeUploadModal}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-sm rounded-lg transition-colors"
                >Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

